/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `device_settings` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DeviceSettingsMesg : Mesg {
    public constructor() : super("DeviceSettings", Profile.MesgNum.DEVICE_SETTINGS)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** active_time_zone. Index into time zone arrays. */
    public var activeTimeZone: UByte?
        get() = getFieldValue(ACTIVE_TIME_ZONE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ACTIVE_TIME_ZONE_FIELD_NUM, value)

    /** utc_offset. Offset from system time. Required to convert timestamp from system time to UTC. */
    public var utcOffset: UInt?
        get() = getFieldValue(UTC_OFFSET_FIELD_NUM) as? UInt
        set(value) = setFieldValue(UTC_OFFSET_FIELD_NUM, value)

    /** Number of values stored in time_offset. */
    public val numTimeOffset: Int
        get() = getField(TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** time_offset, in s. Offset from system time. */
    public var timeOffset: List<UInt?>?
        get() {
            val field = getField(TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeOffset(it) }
        }
        set(value) {
            removeField(TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeOffset(index, element) }
        }

    public fun getTimeOffset(index: Int): UInt? =
        getFieldValue(TIME_OFFSET_FIELD_NUM, index) as? UInt

    public fun setTimeOffset(index: Int, value: UInt?) {
        setFieldValue(TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_mode. */
    public val numTimeMode: Int
        get() = getField(TIME_MODE_FIELD_NUM)?.numValues ?: 0

    /** time_mode. Display mode for the time */
    public var timeMode: List<TimeMode?>?
        get() {
            val field = getField(TIME_MODE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeMode(it) }
        }
        set(value) {
            removeField(TIME_MODE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeMode(index, element) }
        }

    public fun getTimeMode(index: Int): TimeMode? =
        (getFieldValue(TIME_MODE_FIELD_NUM, index) as? UByte)?.let { TimeMode.fromValue(it) }

    public fun setTimeMode(index: Int, value: TimeMode?) {
        setFieldValue(TIME_MODE_FIELD_NUM, value?.value, index)
    }

    /** Number of values stored in time_zone_offset. */
    public val numTimeZoneOffset: Int
        get() = getField(TIME_ZONE_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** time_zone_offset, in hr. timezone offset in 1/4 hour increments */
    public var timeZoneOffset: List<Double?>?
        get() {
            val field = getField(TIME_ZONE_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeZoneOffset(it) }
        }
        set(value) {
            removeField(TIME_ZONE_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeZoneOffset(index, element) }
        }

    public fun getTimeZoneOffset(index: Int): Double? =
        getFieldValue(TIME_ZONE_OFFSET_FIELD_NUM, index) as? Double

    public fun setTimeZoneOffset(index: Int, value: Double?) {
        setFieldValue(TIME_ZONE_OFFSET_FIELD_NUM, value, index)
    }

    /** backlight_mode. Mode for backlight */
    public var backlightMode: BacklightMode?
        get() = (getFieldValue(BACKLIGHT_MODE_FIELD_NUM) as? UByte)?.let { BacklightMode.fromValue(it) }
        set(value) = setFieldValue(BACKLIGHT_MODE_FIELD_NUM, value?.value)

    /** activity_tracker_enabled. Enabled state of the activity tracker functionality */
    public var activityTrackerEnabled: Boolean?
        get() = (getFieldValue(ACTIVITY_TRACKER_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ACTIVITY_TRACKER_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** clock_time. UTC timestamp used to set the devices clock and date */
    public var clockTime: Instant?
        get() = (getFieldValue(CLOCK_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(CLOCK_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** Number of values stored in pages_enabled. */
    public val numPagesEnabled: Int
        get() = getField(PAGES_ENABLED_FIELD_NUM)?.numValues ?: 0

    /** pages_enabled. Bitfield to configure enabled screens for each supported loop */
    public var pagesEnabled: List<UShort?>?
        get() {
            val field = getField(PAGES_ENABLED_FIELD_NUM) ?: return null
            return List(field.numValues) { getPagesEnabled(it) }
        }
        set(value) {
            removeField(PAGES_ENABLED_FIELD_NUM)
            value?.forEachIndexed { index, element -> setPagesEnabled(index, element) }
        }

    public fun getPagesEnabled(index: Int): UShort? =
        getFieldValue(PAGES_ENABLED_FIELD_NUM, index) as? UShort

    public fun setPagesEnabled(index: Int, value: UShort?) {
        setFieldValue(PAGES_ENABLED_FIELD_NUM, value, index)
    }

    /** move_alert_enabled. Enabled state of the move alert */
    public var moveAlertEnabled: Boolean?
        get() = (getFieldValue(MOVE_ALERT_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(MOVE_ALERT_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** date_mode. Display mode for the date */
    public var dateMode: DateMode?
        get() = (getFieldValue(DATE_MODE_FIELD_NUM) as? UByte)?.let { DateMode.fromValue(it) }
        set(value) = setFieldValue(DATE_MODE_FIELD_NUM, value?.value)

    /** display_orientation. */
    public var displayOrientation: DisplayOrientation?
        get() = (getFieldValue(DISPLAY_ORIENTATION_FIELD_NUM) as? UByte)?.let { DisplayOrientation.fromValue(it) }
        set(value) = setFieldValue(DISPLAY_ORIENTATION_FIELD_NUM, value?.value)

    /** mounting_side. */
    public var mountingSide: Side?
        get() = (getFieldValue(MOUNTING_SIDE_FIELD_NUM) as? UByte)?.let { Side.fromValue(it) }
        set(value) = setFieldValue(MOUNTING_SIDE_FIELD_NUM, value?.value)

    /** Number of values stored in default_page. */
    public val numDefaultPage: Int
        get() = getField(DEFAULT_PAGE_FIELD_NUM)?.numValues ?: 0

    /** default_page. Bitfield to indicate one page as default for each supported loop */
    public var defaultPage: List<UShort?>?
        get() {
            val field = getField(DEFAULT_PAGE_FIELD_NUM) ?: return null
            return List(field.numValues) { getDefaultPage(it) }
        }
        set(value) {
            removeField(DEFAULT_PAGE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setDefaultPage(index, element) }
        }

    public fun getDefaultPage(index: Int): UShort? =
        getFieldValue(DEFAULT_PAGE_FIELD_NUM, index) as? UShort

    public fun setDefaultPage(index: Int, value: UShort?) {
        setFieldValue(DEFAULT_PAGE_FIELD_NUM, value, index)
    }

    /** autosync_min_steps, in steps. Minimum steps before an autosync can occur */
    public var autosyncMinSteps: UShort?
        get() = getFieldValue(AUTOSYNC_MIN_STEPS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AUTOSYNC_MIN_STEPS_FIELD_NUM, value)

    /** autosync_min_time, in minutes. Minimum minutes before an autosync can occur */
    public var autosyncMinTime: UShort?
        get() = getFieldValue(AUTOSYNC_MIN_TIME_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AUTOSYNC_MIN_TIME_FIELD_NUM, value)

    /** lactate_threshold_autodetect_enabled. Enable auto-detect setting for the lactate threshold feature. */
    public var lactateThresholdAutodetectEnabled: Boolean?
        get() = (getFieldValue(LACTATE_THRESHOLD_AUTODETECT_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(LACTATE_THRESHOLD_AUTODETECT_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** ble_auto_upload_enabled. Automatically upload using BLE */
    public var bleAutoUploadEnabled: Boolean?
        get() = (getFieldValue(BLE_AUTO_UPLOAD_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(BLE_AUTO_UPLOAD_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** auto_sync_frequency. Helps to conserve battery by changing modes */
    public var autoSyncFrequency: AutoSyncFrequency?
        get() = (getFieldValue(AUTO_SYNC_FREQUENCY_FIELD_NUM) as? UByte)?.let { AutoSyncFrequency.fromValue(it) }
        set(value) = setFieldValue(AUTO_SYNC_FREQUENCY_FIELD_NUM, value?.value)

    /** auto_activity_detect. Allows setting specific activities auto-activity detect enabled/disabled settings */
    public var autoActivityDetect: UInt?
        get() = getFieldValue(AUTO_ACTIVITY_DETECT_FIELD_NUM) as? UInt
        set(value) = setFieldValue(AUTO_ACTIVITY_DETECT_FIELD_NUM, value)

    /** number_of_screens. Number of screens configured to display */
    public var numberOfScreens: UByte?
        get() = getFieldValue(NUMBER_OF_SCREENS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(NUMBER_OF_SCREENS_FIELD_NUM, value)

    /** smart_notification_display_orientation. Smart Notification display orientation */
    public var smartNotificationDisplayOrientation: DisplayOrientation?
        get() = (getFieldValue(SMART_NOTIFICATION_DISPLAY_ORIENTATION_FIELD_NUM) as? UByte)?.let { DisplayOrientation.fromValue(it) }
        set(value) = setFieldValue(SMART_NOTIFICATION_DISPLAY_ORIENTATION_FIELD_NUM, value?.value)

    /** tap_interface. */
    public var tapInterface: Switch?
        get() = (getFieldValue(TAP_INTERFACE_FIELD_NUM) as? UByte)?.let { Switch.fromValue(it) }
        set(value) = setFieldValue(TAP_INTERFACE_FIELD_NUM, value?.value)

    /** tap_sensitivity. Used to hold the tap threshold setting */
    public var tapSensitivity: TapSensitivity?
        get() = (getFieldValue(TAP_SENSITIVITY_FIELD_NUM) as? UByte)?.let { TapSensitivity.fromValue(it) }
        set(value) = setFieldValue(TAP_SENSITIVITY_FIELD_NUM, value?.value)

    public companion object {
        public const val ACTIVE_TIME_ZONE_FIELD_NUM: UByte = 0u
        public const val UTC_OFFSET_FIELD_NUM: UByte = 1u
        public const val TIME_OFFSET_FIELD_NUM: UByte = 2u
        public const val TIME_MODE_FIELD_NUM: UByte = 4u
        public const val TIME_ZONE_OFFSET_FIELD_NUM: UByte = 5u
        public const val BACKLIGHT_MODE_FIELD_NUM: UByte = 12u
        public const val ACTIVITY_TRACKER_ENABLED_FIELD_NUM: UByte = 36u
        public const val CLOCK_TIME_FIELD_NUM: UByte = 39u
        public const val PAGES_ENABLED_FIELD_NUM: UByte = 40u
        public const val MOVE_ALERT_ENABLED_FIELD_NUM: UByte = 46u
        public const val DATE_MODE_FIELD_NUM: UByte = 47u
        public const val DISPLAY_ORIENTATION_FIELD_NUM: UByte = 55u
        public const val MOUNTING_SIDE_FIELD_NUM: UByte = 56u
        public const val DEFAULT_PAGE_FIELD_NUM: UByte = 57u
        public const val AUTOSYNC_MIN_STEPS_FIELD_NUM: UByte = 58u
        public const val AUTOSYNC_MIN_TIME_FIELD_NUM: UByte = 59u
        public const val LACTATE_THRESHOLD_AUTODETECT_ENABLED_FIELD_NUM: UByte = 80u
        public const val BLE_AUTO_UPLOAD_ENABLED_FIELD_NUM: UByte = 86u
        public const val AUTO_SYNC_FREQUENCY_FIELD_NUM: UByte = 89u
        public const val AUTO_ACTIVITY_DETECT_FIELD_NUM: UByte = 90u
        public const val NUMBER_OF_SCREENS_FIELD_NUM: UByte = 94u
        public const val SMART_NOTIFICATION_DISPLAY_ORIENTATION_FIELD_NUM: UByte = 95u
        public const val TAP_INTERFACE_FIELD_NUM: UByte = 134u
        public const val TAP_SENSITIVITY_FIELD_NUM: UByte = 174u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("ActiveTimeZone", 0u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[1] = Field("UtcOffset", 1u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[2] = Field("TimeOffset", 2u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[4] = Field("TimeMode", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("TimeZoneOffset", 5u, BaseType.SINT8, 4.0, 0.0, "hr", false)
            fields[12] = Field("BacklightMode", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[36] = Field("ActivityTrackerEnabled", 36u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[39] = Field("ClockTime", 39u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[40] = Field("PagesEnabled", 40u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[46] = Field("MoveAlertEnabled", 46u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[47] = Field("DateMode", 47u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[55] = Field("DisplayOrientation", 55u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[56] = Field("MountingSide", 56u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[57] = Field("DefaultPage", 57u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[58] = Field("AutosyncMinSteps", 58u, BaseType.UINT16, 1.0, 0.0, "steps", false)
            fields[59] = Field("AutosyncMinTime", 59u, BaseType.UINT16, 1.0, 0.0, "minutes", false)
            fields[80] = Field("LactateThresholdAutodetectEnabled", 80u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[86] = Field("BleAutoUploadEnabled", 86u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[89] = Field("AutoSyncFrequency", 89u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[90] = Field("AutoActivityDetect", 90u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[94] = Field("NumberOfScreens", 94u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[95] = Field("SmartNotificationDisplayOrientation", 95u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[134] = Field("TapInterface", 134u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[174] = Field("TapSensitivity", 174u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
